import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { Cacheable } from "ts-cacheable";

import { environment } from "@environments/environment";

import { ShareMessageResponse } from "@shared/Interfaces/share-message-response.interface";
import { ParamsService } from "@shared/Services/params.service";

import { Dropdown } from "@salud/shared/Types/dropdown.enum";
import { IdentificationCode } from "@salud/shared/Types/identification-code.enum";
import { OptionsType } from "@salud/shared/Types/options-type.interface";
import facturas from "@salud/shared/Utils/cantidad-facturas.json";

import { DetalleFactura } from "../Types/Interfaces/detalle-factura.interface";
import { CreditNoteEmail, FacturaEmail } from "../Types/Interfaces/factura-email.interface";
import { Factura, FacturaDto, FacturaStatus, FacturaType } from "../Types/Interfaces/factura.interface";
import { FacturasFilter, DetalleFacturasFilter } from "../Types/Interfaces/facturas-filter.interface";
import { FacturaReportFilter, NotaCreditoReportFilter } from "../Types/Interfaces/get-facturas-report.inerface";

const { enlineaBackendUrl } = environment;

@Injectable({
  providedIn: "root",
})
export class FacturasRepository {
  constructor(private http: HttpClient, private paramsService: ParamsService) {}

  public getFacturaTypes(): Observable<FacturaType[]> {
    return this.http.get<FacturaType[]>(`${enlineaBackendUrl}/api/Comunes/DropDown/${Dropdown.TipoFactura}`);
  }

  public getFacturaStatus(): Observable<FacturaStatus[]> {
    return this.http.get<FacturaStatus[]>(`${enlineaBackendUrl}/api/Comunes/DropDown/${Dropdown.EstadoFactura}`);
  }

  public getFacturasQuantities(): Observable<OptionsType[]> {
    return of(facturas.cantidad);
  }

  public sendCreditNoteEmail(request: CreditNoteEmail): Observable<ShareMessageResponse> {
    return this.http.post<ShareMessageResponse>(`${enlineaBackendUrl}/api/ars/CreditNotes/email`, request);
  }

  public sendFacturaEmail(request: FacturaEmail): Observable<ShareMessageResponse> {
    return this.http.post<ShareMessageResponse>(`${enlineaBackendUrl}/api/ars/ShareInvoices/email`, request);
  }

  public getFacturas(facturasRequest: FacturasFilter): Observable<Factura[]> {
    const params = this.paramsService.mapRequestParams(facturasRequest);
    return this.http
      .get<FacturaDto[]>(`${enlineaBackendUrl}/api/ars/Intermediarios/NotasCreditoFacturas`, {
        params,
      })
      .pipe(
        map((facturasDtos) => {
          return facturasDtos.map((facturaDto) => {
            const identificacionDtoValues = facturaDto.identificacion.split("-");

            const identificacion = identificacionDtoValues[1];
            const identificationType: IdentificationCode = identificacionDtoValues[0] as IdentificationCode;
            return {
              ...facturaDto,
              identificacion,
              tipoIdentificacion: identificationType,
              fullIdentification: `${identificationType}-${identificacion} `,
              period: `Desde ${facturaDto.fechaDesde} Hasta ${facturaDto.fechaHasta}`,
            };
          });
        })
      );
  }

  public getDetalleFacturas(detalleFacturasFilter: DetalleFacturasFilter): Observable<DetalleFactura[]> {
    const params = this.paramsService.mapRequestParams(detalleFacturasFilter);

    return this.http
      .get<DetalleFactura[]>(`${enlineaBackendUrl}/api/ars/Intermediarios/DetalleFacturas`, {
        params,
      })
      .pipe(
        map((facturas) => {
          return facturas.map((factura) => ({ ...factura, period: `Desde ${factura.fechaDesde} Hasta ${factura.fechaHasta}` }));
        })
      );
  }

  public getFacturasReport(reportFilter: FacturaReportFilter): Observable<Blob> {
    const params = new HttpParams({ fromObject: reportFilter as any });
    return this.http.get<Blob>(`${enlineaBackendUrl}/api/ars/Intermediarios/ReporteFactura`, {
      params,
      responseType: "blob" as "json",
    });
  }

  public getNotaCreditoReport({ numeroFactura }: NotaCreditoReportFilter): Observable<Blob> {
    const params = new HttpParams({ fromObject: { numeroFactura } });
    return this.http.get<Blob>(`${enlineaBackendUrl}/api/ars/Intermediarios/ReporteNotaCredito`, {
      params,
      responseType: "blob" as "json",
    });
  }
}
